import {
  Box,
  Button,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Link,
  Select,
  Table,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
  Icon,
  useToast
} from '@chakra-ui/react';
import ReviewSearchTab from './ReviewSearchTab';
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FaSort, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { MdSearch, MdClear } from 'react-icons/md';
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { ApplicationPathName, PurposeType, getPurposeTypeNameByCode, getEquipmentUsageTypeNameByCode, EquipmentUsageType } from '../../contexts/constants';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { OrganizationWithHierarchyType } from '../../contexts/types';
import { hasFilterString, hasFilterDateRange, formatMultiValue, openInvestmentScreeningDetailInNewTab } from '../../utils/utils';
import TruncatedCell from '../../components/TruncatedCell';
import DebouncedInput from '../../components/DebouncedInput';
import { getHeadquartersNameFromHierarchy, getDivisionNameFromHierarchy } from '../../utils/i18nDisplay';

type SortConfig = { key: string; ascending: boolean };

/** Date → input[type=date]用文字列 */
const formatDateForInput = (d: Date | null): string => {
  if (!d || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
/** input[type=date]文字列 → Date */
const parseDateFromInput = (s: string): Date | null => {
  if (!s) return null;
  const d = new Date(s + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
};

/** 列リサイズハンドル */
const ResizeHandle: React.FC<{ colIndex: number; setColWidths: React.Dispatch<React.SetStateAction<number[]>> }> = ({ colIndex, setColWidths }) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const th = (e.target as HTMLElement).closest("th");
    if (!th) return;
    const table = th.closest("table");
    if (!table) return;
    const ths = Array.from(table.querySelectorAll("thead th"));
    const startWidth = th.getBoundingClientRect().width;
    setColWidths(ths.map(t => t.getBoundingClientRect().width));
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      setColWidths(prev => {
        const next = [...prev];
        next[colIndex] = Math.max(50, startWidth + delta);
        return next;
      });
    };
    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  return (
    <Box
      position="absolute"
      right="-2px"
      top={0}
      bottom={0}
      width="5px"
      cursor="col-resize"
      onMouseDown={handleMouseDown}
      _hover={{ bg: "blue.300" }}
      zIndex={2}
    />
  );
};

/**
 * 投資状況検索画面
 */
const InvestmentList: React.FC = () => {

  // 翻訳
  const { t } = useTranslation();
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext);

  // -----------------------------
  // useState
  // -----------------------------
  // （検索条件）申請番号
  const [conditionApplicationNumber, setConditionApplicationNumber] = useState<string>("");
  // （検索条件）設備投資PROJECT名
  const [conditionProjectName, setConditionProjectName] = useState<string>("");
  // （検索条件）案件名
  const [conditionOpportunityName, setConditionOpportunityName] = useState<string>("");
  // （検索条件）MMI製品名
  const [conditionMmiProductName, setConditionMmiProductName] = useState<string>("");
  // （検索条件）起票開始日
  const [conditionStartDate, setConditionStartDate] = useState(() => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 1); // 1年前に設定
    return today.toISOString().split("T")[0]; // "YYYY-MM-DD"
  });
  // （検索条件）起票終了日
  const [conditionEndDate, setConditionEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // "YYYY-MM-DD"
  });
  // （検索条件）適用年月を管理
  const [conditionFiscalPeriod, setConditionFiscalPeriod] = useState<string>(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  });
  // （検索条件）事業本部を管理
  const [conditionHeadquarters, setConditionHeadquarters] = useState<string>("");
  // （検索条件）事業部を管理
  const [conditionDivisionOrg, setConditionDivisionOrg] = useState<string>("");
  // （フィルター）設備投資PROJECT名を管理
  const [filterProjectName, setFilterProjectName] = useState<string>("");
  // （フィルター）目的を管理
  const [filterPurpose, setFilterPurpose] = useState<string>("");
  // （フィルター）設備用途区分を管理
  const [filterEquipmentUsageType, setFilterEquipmentUsageType] = useState<string>("");
  // （フィルター）事業本部を管理
  const [filterHeadquarters, setFilterHeadquarters] = useState<string>("");
  // （フィルター）事業部を管理
  const [filterDivision, setFilterDivision] = useState<string>("");
  // （フィルター）顧客名を管理
  const [filterCustomerName, setFilterCustomerName] = useState<string>("");
  // （フィルター）案件名を管理
  const [filterOpportunityName, setFilterOpportunityName] = useState<string>("");
  // （フィルター）申請番号を管理
  const [filterApplicationNumber, setFilterApplicationNumber] = useState<string>("");
  const [filterEquipmentPlanNumber, setFilterEquipmentPlanNumber] = useState<string>("");
  // （フィルター）MMI製品名を管理
  const [filterMMIProductName, setFilterMMIProductName] = useState<string>("");
  // （フィルター）顧客製品名を管理
  const [filterCustomerProductName, setFilterCustomerProductName] = useState<string>("");
  // （フィルター）設置工場を管理
  const [filterInstallationFactory, setFilterInstallationFactory] = useState<string>("");
  // （フィルター）記入者を管理
  const [filterRegistrant, setFilterRegistrant] = useState<string>("");
  // （フィルター）承認開始日を管理
  const [filterApplicationStartDate, setFilterApplicationStartDate] = useState<Date | null>(null);
  // （フィルター）承認終了日を管理
  const [filterApplicationEndDate, setFilterApplicationEndDate] = useState<Date | null>(null);
  // （ソート）
  const [sort, setSort] = useState<SortConfig>({ key: "approveDay", ascending: false });
  // 列幅リサイズ用
  const [colWidths, setColWidths] = useState<number[]>([]);
  // DBから取得した経理組織情報を管理
  const [accountingOrganizations, setAccountingOrganizations] = useState<OrganizationWithHierarchyType[]>([]);
  // DBから取得した投資状況一覧を管理
  const [investmentTargets, setInvestmentTargets] = useState<any[]>([]);

  // ソートボタン描画
  const renderSortButton = (column: string) => (
    <IconButton
      h="16px"
      onClick={() => setSort(sort.key === column ? { key: column, ascending: !sort.ascending } : { key: column, ascending: true })}
      _hover={{ bg: "gray.100", cursor: "pointer" }}
      icon={sort.key === column ? (sort.ascending ? <FaSortAmountUp /> : <FaSortAmountDown />) : <FaSort />}
      aria-label={t('common.label.sort')}
    />
  );

  // ソート値取得
  const getSortValue = (item: any, key: string): string => {
    switch (key) {
      case "projectName": return item.projectName || "";
      case "purpose": return item.purpose || "";
      case "equipmentUsageType": return getEquipmentUsageTypeNameByCode(item.equipmentUsageType || "") || "";
      case "headquarters": return getHeadquartersNameFromHierarchy(item);
      case "division": return getDivisionNameFromHierarchy(item);
      case "customerName": return item.customerName || "";
      case "opportunityName": return item.opportunityName || "";
      case "applicationNumber": return item.applicationNumber || "";
      case "equipmentPlanNumber": return formatMultiValue(item.equipmentPlanNumber);
      case "mmiProductName": return item.mmiProductName || "";
      case "customerProductName": return item.customerProductName || "";
      case "installationFactory": return item.installationFactory || "";
      case "registrant": return item.registrant || "";
      case "approveDay": return item.approveDay || "";
      default: return "";
    }
  };

  // -----------------------------
  // API
  // -----------------------------
  // 経理組織情報取得API
  const getAccountingOrganization = async (fiscalPeriod: string) => {
    const functionName = "getAccountingOrganization";
    try {
      console.log(`${functionName} START`);
      const response = await WebAPI.request({
        query: `
          query getAccountingOrganization($hasDeleted: Boolean, $fiscalPeriod: String) {
            getAccountingOrganization(hasDeleted: $hasDeleted, fiscalPeriod: $fiscalPeriod) {
              organizationId
              organizationName
              parentOrganizationId
              parentOrganizationName
              fiscalPeriod
            }
          }
        `,
        variables: {
          hasDeleted: false,
          fiscalPeriod: fiscalPeriod || undefined,
        },
      });
      console.log(`${functionName} RESPONSE:`, response);
      return response?.data?.getAccountingOrganization as OrganizationWithHierarchyType[] | undefined;
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return undefined;
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // プロジェクト検索API（統合版）
  const getProjectSearch = async () => {
    const functionName = 'getProjectSearch';
    try {
      console.log(`${functionName} START`);
      const response = await WebAPI.request({
        query: `
          query getProjectSearch(
            $searchType: String!,
            $filterProjectName: String,
            $filterOpportunityName: String,
            $filterMMIProductName: String,
            $filterOrganizationIds: String,
            $filterStartDate: String,
            $filterEndDate: String,
            $filterApplicationNumber: String
          ) {
            getProjectSearch(
              searchType: $searchType,
              filterProjectName: $filterProjectName,
              filterOpportunityName: $filterOpportunityName,
              filterMMIProductName: $filterMMIProductName,
              filterOrganizationIds: $filterOrganizationIds,
              filterStartDate: $filterStartDate,
              filterEndDate: $filterEndDate,
              filterApplicationNumber: $filterApplicationNumber
            ) {
              projectId
              projectName
              customerName
              opportunityName
              mmiProductName
              applicationNumber
              customerProductName
              installationFactory
              registrant
              approveDay
              purpose
              equipmentUsageType
              divisionHierarchy
              divisionHierarchyEn
              equipmentPlanNumber
            }
          }
        `,
        variables: {
          searchType: 'investment',
          filterProjectName: conditionProjectName,
          filterOpportunityName: conditionOpportunityName,
          filterMMIProductName: conditionMmiProductName,
          filterOrganizationIds: buildFilterOrganizationIds(),
          filterStartDate: conditionStartDate,
          filterEndDate: conditionEndDate,
          filterApplicationNumber: conditionApplicationNumber
        }
      });
      console.log(`${functionName} RESPONSE:`, response);
      return response?.data?.getProjectSearch;
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return undefined;
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // -----------------------------
  // useEffect
  // -----------------------------
  useEffect(() => {
    fetchAccountingOrganizations(conditionFiscalPeriod);
  }, []);

  // -----------------------------
  // 業務ロジック
  // -----------------------------
  /**
   * 経理組織情報を取得
   * @param fiscalPeriod 適用年月（YYYY-MM形式）
   */
  const fetchAccountingOrganizations = async (fiscalPeriod: string) => {
    const orgData = await getAccountingOrganization(fiscalPeriod);
    if (orgData) {
      setAccountingOrganizations(orgData);
    }
    // 事業本部・事業部の選択をリセット
    setConditionHeadquarters("");
    setConditionDivisionOrg("");
  };

  /**
   * 投資状況一覧のフィルタリング及びソート
   *
   * @returns {any[]} フィルタリング及び並び替え後の投資状況一覧
   */
  const filteredInvestmentTargets = useMemo(() => (investmentTargets ?? []).filter((investmentTarget) => {
    if (!investmentTarget) {
      return false;
    }
    const headquarters = getHeadquartersNameFromHierarchy(investmentTarget);
    const division = getDivisionNameFromHierarchy(investmentTarget);
    return (
      hasFilterString(investmentTarget.projectName, filterProjectName) &&
      hasFilterString(investmentTarget.purpose, filterPurpose) &&
      hasFilterString(getEquipmentUsageTypeNameByCode(investmentTarget.equipmentUsageType || ""), filterEquipmentUsageType) &&
      hasFilterString(headquarters, filterHeadquarters) &&
      hasFilterString(division, filterDivision) &&
      hasFilterString(investmentTarget.customerName, filterCustomerName) &&
      hasFilterString(investmentTarget.opportunityName, filterOpportunityName) &&
      hasFilterString(investmentTarget.mmiProductName, filterMMIProductName) &&
      hasFilterString(investmentTarget.applicationNumber, filterApplicationNumber) &&
      hasFilterString(formatMultiValue(investmentTarget.equipmentPlanNumber), filterEquipmentPlanNumber) &&
      hasFilterString(investmentTarget.customerProductName, filterCustomerProductName) &&
      hasFilterString(investmentTarget.installationFactory, filterInstallationFactory) &&
      hasFilterString(investmentTarget.registrant, filterRegistrant) &&
      hasFilterDateRange(investmentTarget.approveDay, filterApplicationStartDate, filterApplicationEndDate)
    );
  }).sort((a, b) => {
    const valA = getSortValue(a, sort.key);
    const valB = getSortValue(b, sort.key);
    const cmp = valA.localeCompare(valB);
    return sort.ascending ? cmp : -cmp;
  }), [investmentTargets, filterProjectName, filterPurpose, filterEquipmentUsageType, filterHeadquarters, filterDivision, filterCustomerName, filterOpportunityName, filterMMIProductName, filterApplicationNumber, filterEquipmentPlanNumber, filterCustomerProductName, filterInstallationFactory, filterRegistrant, filterApplicationStartDate, filterApplicationEndDate, sort]);

  // -----------------------------
  // EventHandler
  // -----------------------------
  // 承認開始日選択処理
  const handleStartDate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setConditionStartDate(event.target.value);
  }

  // 承認終了日選択処理
  const handleEndDate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setConditionEndDate(event.target.value);
  }

  // 適用年月変更処理
  const handleFiscalPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConditionFiscalPeriod(value);
    if (value) {
      fetchAccountingOrganizations(value);
    }
  };

  // 適用年月を今月にリセット（振り返り検索タブのクリアボタンから使用）
  const handleResetFiscalPeriod = () => {
    const today = new Date();
    const defaultFiscalPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    if (conditionFiscalPeriod !== defaultFiscalPeriod) {
      setConditionFiscalPeriod(defaultFiscalPeriod);
      fetchAccountingOrganizations(defaultFiscalPeriod);
    }
  };

  // 事業本部変更処理
  const handleHeadquartersChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setConditionHeadquarters(e.target.value);
    setConditionDivisionOrg(""); // 事業本部変更時に事業部をリセット
  };

  // 事業部リスト（事業本部の配下を算出）
  const divisionOptions = useMemo(() => {
    if (!conditionHeadquarters) return [];
    const departments = accountingOrganizations.filter(o => o.parentOrganizationId === conditionHeadquarters);
    return departments.flatMap(dept =>
      accountingOrganizations.filter(o => o.parentOrganizationId === dept.organizationId)
    );
  }, [accountingOrganizations, conditionHeadquarters]);

  /**
   * 選択された組織の子孫IDを再帰的に収集し、カンマ区切り文字列で返す
   * 事業本部のみ選択: 事業本部 + 配下の全子孫ID
   * 事業部まで選択: 事業部 + 配下の全子孫ID
   */
  const buildFilterOrganizationIds = (): string => {
    if (!conditionHeadquarters) return "";
    const rootOrgId = conditionDivisionOrg || conditionHeadquarters;

    const collectDescendantIds = (parentId: string): string[] => {
      const children = accountingOrganizations.filter(o => o.parentOrganizationId === parentId);
      return children.flatMap(c => [c.organizationId, ...collectDescendantIds(c.organizationId)]);
    };

    return [rootOrgId, ...collectDescendantIds(rootOrgId)].join(",");
  };

  // 検索ボタン押下処理
  const handleSearch = async () => {
    // フィルター初期化
    setFilterProjectName("");
    setFilterPurpose("");
    setFilterEquipmentUsageType("");
    setFilterHeadquarters("");
    setFilterDivision("");
    setFilterCustomerName("");
    setFilterOpportunityName("");
    setFilterMMIProductName("");
    setFilterApplicationNumber("");
    setFilterEquipmentPlanNumber("");
    setFilterCustomerProductName("");
    setFilterInstallationFactory("");
    setFilterRegistrant("");
    setFilterApplicationStartDate(null);
    setFilterApplicationEndDate(null);
    // 投資状況一覧取得API実行
    const investmentProjects = await getProjectSearch();
    if (investmentProjects) {
      // 正常に取得できた場合
      const investmentTargetList: any[] = investmentProjects.map((p: any) => ({
        ...p,
        purpose: getPurposeTypeNameByCode(p.purpose),
      }));
      setInvestmentTargets(investmentTargetList);
      if (investmentTargetList.length === 0) {
        toast({
          title: t('common.message.searchResult'),
          description: t('common.message.noDataFound'),
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      // データ取得失敗の場合
      toast({
        title: t('common.message.searchExecutionError'),
        description: t('features.Investment.List.message.searchFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  // 検索条件クリアボタン押下処理
  const handleClearCondition = async () => {
    setConditionApplicationNumber("");
    setConditionProjectName("");
    setConditionOpportunityName("");
    setConditionMmiProductName("");
    const today = new Date();
    setConditionEndDate(today.toISOString().split("T")[0]);
    today.setFullYear(today.getFullYear() - 1);
    setConditionStartDate(today.toISOString().split("T")[0]);
    const today2 = new Date();
    const defaultFiscalPeriod = `${today2.getFullYear()}-${String(today2.getMonth() + 1).padStart(2, "0")}`;
    if (conditionFiscalPeriod !== defaultFiscalPeriod) {
      setConditionFiscalPeriod(defaultFiscalPeriod);
      fetchAccountingOrganizations(defaultFiscalPeriod);
    }
    setConditionHeadquarters("");
    setConditionDivisionOrg("");
  }

  return (
    <Box h="100%" display="flex" flexDirection="column" p={4} bg="gray.50">
      <Tabs
        variant="enclosed"
        colorScheme="blue"
        isLazy
        display="flex"
        flexDirection="column"
        flex={1}
        overflow="hidden"
      >
        <TabList>
          <Tab>{t('features.Investment.List.tab.applicationSearch')}</Tab>
          <Tab>{t('features.Investment.List.tab.reviewSearch')}</Tab>
        </TabList>
        <TabPanels flex={1} display="flex" flexDirection="column" overflow="hidden">
          <TabPanel p={0} pt={4} display="flex" flexDirection="column" gap={4} flex={1} overflow="auto">
            <Box bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4}>
              <Wrap spacing={4}>
                <WrapItem>
                  <Box>
                    <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>{t('common.field.applicationNumber')}</FormLabel>
                    <Input
                      name="applicationNumber"
                      value={conditionApplicationNumber}
                      onChange={(e) => { setConditionApplicationNumber(e.target.value) }}
                      size="sm"
                      placeholder={t('common.placeholder.partialMatch')}
                    />
                  </Box>
                </WrapItem>
                <WrapItem>
                  <Box>
                    <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>{t('common.field.projectName')}</FormLabel>
                    <Input
                      name="productName"
                      value={conditionProjectName}
                      onChange={(e) => { setConditionProjectName(e.target.value) }}
                      size="sm"
                      placeholder={t('common.placeholder.partialMatch')}
                    />
                  </Box>
                </WrapItem>
                <WrapItem>
                  <Box>
                    <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>{t('common.field.opportunityName')}</FormLabel>
                    <Input
                      name="productName"
                      value={conditionOpportunityName}
                      onChange={(e) => { setConditionOpportunityName(e.target.value) }}
                      size="sm"
                      placeholder={t('common.placeholder.partialMatch')}
                    />
                  </Box>
                </WrapItem>
                <WrapItem>
                  <Box>
                    <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={3}>{t('common.field.mmiProductName')}</FormLabel>
                    <Input
                      name="productName"
                      value={conditionMmiProductName}
                      onChange={(e) => { setConditionMmiProductName(e.target.value) }}
                      size="sm"
                      placeholder={t('common.placeholder.partialMatch')}
                    />
                  </Box>
                </WrapItem>
              </Wrap>
              <Wrap spacing={4} mt={3} align="flex-end">
                <WrapItem>
                  <Box>
                    <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={1}>{t('common.field.approveDate')}</FormLabel>
                    <Flex align="center" gap={2}>
                      <Box position="relative" mt={2}>
                        <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>{t('common.field.startDate')}</Text>
                        <Input
                          type="date"
                          size="sm"
                          value={conditionStartDate}
                          onChange={(event) => {
                            if (event.target.value.length > 10) {
                              return;
                            }
                            handleStartDate(event);
                          }}
                        />
                      </Box>
                      <Text fontSize="sm">～</Text>
                      <Box position="relative" mt={2}>
                        <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>{t('common.field.endDate')}</Text>
                        <Input
                          type="date"
                          size="sm"
                          value={conditionEndDate}
                          onChange={(event) => {
                            if (event.target.value.length > 10) {
                              return;
                            }
                            handleEndDate(event);
                          }}
                        />
                      </Box>
                    </Flex>
                  </Box>
                </WrapItem>
                <WrapItem>
                  <Box>
                    <FormLabel fontSize="sm" color="gray.600" fontWeight="semibold" mb={1}>{t('common.field.managementStructure')}</FormLabel>
                    <Flex gap={3} align="center">
                      <Box position="relative" mt={2}>
                        <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>{t('common.field.applicablePeriod')}</Text>
                        <Input
                          type="month"
                          size="sm"
                          value={conditionFiscalPeriod}
                          onChange={handleFiscalPeriodChange}
                        />
                      </Box>
                      <Box position="relative" mt={2}>
                        <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>{t('common.field.headquarters')}</Text>
                        <Select
                          w="200px"
                          value={conditionHeadquarters}
                          onChange={handleHeadquartersChange}
                          size="sm"
                        >
                          <option value=""></option>
                          {accountingOrganizations
                            .filter(o => o.parentOrganizationId === "organization00000")
                            .map(o => (
                              <option key={o.organizationId} value={o.organizationId}>
                                {o.organizationName}
                              </option>
                            ))}
                        </Select>
                      </Box>
                      <Box position="relative" mt={2}>
                        <Text position="absolute" top="-9px" left="8px" bg="white" px={1} fontSize="xs" color="gray.500" zIndex={1}>{t('common.field.division')}</Text>
                        <Select
                          w="200px"
                          value={conditionDivisionOrg}
                          onChange={(e) => setConditionDivisionOrg(e.target.value)}
                          size="sm"
                          disabled={!conditionHeadquarters}
                        >
                          <option value=""></option>
                          {divisionOptions.map(o => (
                            <option key={o.organizationId} value={o.organizationId}>
                              {o.organizationName}
                            </option>
                          ))}
                        </Select>
                      </Box>
                    </Flex>
                  </Box>
                </WrapItem>
              </Wrap>
              <Flex justify="flex-end" mt={3} gap={2}>
                <Button leftIcon={<Icon as={MdSearch} />} size="sm" colorScheme="blue" onClick={() => { handleSearch(); }}>
                  {t('common.button.search')}
                </Button>
                <Button leftIcon={<Icon as={MdClear} />} size="sm" variant="outline" colorScheme="red" onClick={() => { handleClearCondition(); }}>
                  {t('common.button.searchClear')}
                </Button>
              </Flex>
            </Box>
            <Box flex={1} minH={0} bg="white" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden" display="flex" flexDirection="column">
              <Box px={4} py={2} borderBottom="1px solid" borderColor="gray.100" bg="white" position="relative" zIndex={2} flexShrink={0}>
                <Text fontSize="sm" color="gray.600">{t('common.message.searchResult')}: <Text as="span" fontWeight="bold" color="gray.800">{filteredInvestmentTargets.length}</Text> {t('common.label.count')}</Text>
              </Box>
              <Box flex={1} minH={0} overflow="auto">
                <Table borderX="1px solid" borderBottom="1px solid" borderColor="gray.200" variant="simple" size="sm"
                  sx={{ tableLayout: 'fixed' }}
                  width={colWidths.length > 0 ? `${colWidths.reduce((a, b) => a + b, 0)}px` : "149%"}
                >
                  <Thead position="sticky" top={0} zIndex={1} sx={{ 'th input, th select': { ml: '-2px' } }}>
                    <Tr bgColor="gray.50">
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[0] ? `${colWidths[0]}px` : "22%"} position="relative">
                        {t('common.field.projectName')}{renderSortButton("projectName")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterProjectName} onChange={setFilterProjectName} />
                        <ResizeHandle colIndex={0} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[1] ? `${colWidths[1]}px` : "10%"} position="relative">
                        {t('common.field.purpose')}{renderSortButton("purpose")}
                        <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterPurpose} onChange={(e) => setFilterPurpose(e.target.value)}>
                          <option value="">{t('common.label.all')}</option>
                          {Object.values(PurposeType).map(({ code, name }) => <option key={code} value={name}>{getPurposeTypeNameByCode(code)}</option>)}
                        </Select>
                        <ResizeHandle colIndex={1} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[2] ? `${colWidths[2]}px` : "9%"} position="relative">
                        <Box as="span" whiteSpace="nowrap">{t('features.Investment.List.tableHeader.equipmentUsageType')}{renderSortButton("equipmentUsageType")}</Box>
                        <Select size="xs" mt={1} bg="white" fontWeight="normal" value={filterEquipmentUsageType} onChange={(e) => setFilterEquipmentUsageType(e.target.value)}>
                          <option value="">{t('common.label.all')}</option>
                          {Object.values(EquipmentUsageType).map(({ code }) => <option key={code} value={getEquipmentUsageTypeNameByCode(code)}>{getEquipmentUsageTypeNameByCode(code)}</option>)}
                        </Select>
                        <ResizeHandle colIndex={2} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[3] ? `${colWidths[3]}px` : "12%"} position="relative">
                        {t('common.field.headquarters')}{renderSortButton("headquarters")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterHeadquarters} onChange={setFilterHeadquarters} />
                        <ResizeHandle colIndex={3} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[4] ? `${colWidths[4]}px` : "12%"} position="relative">
                        {t('common.field.division')}{renderSortButton("division")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterDivision} onChange={setFilterDivision} />
                        <ResizeHandle colIndex={4} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[5] ? `${colWidths[5]}px` : "11%"} position="relative">
                        {t('common.field.customerName')}{renderSortButton("customerName")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterCustomerName} onChange={setFilterCustomerName} />
                        <ResizeHandle colIndex={5} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[6] ? `${colWidths[6]}px` : "11%"} position="relative">
                        {t('common.field.opportunityName')}{renderSortButton("opportunityName")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterOpportunityName} onChange={setFilterOpportunityName} />
                        <ResizeHandle colIndex={6} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[7] ? `${colWidths[7]}px` : "8%"} position="relative">
                        {t('common.field.applicationNumber')}{renderSortButton("applicationNumber")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterApplicationNumber} onChange={setFilterApplicationNumber} />
                        <ResizeHandle colIndex={7} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[8] ? `${colWidths[8]}px` : "10%"} position="relative">
                        {t('common.field.equipmentPlanNumber')}{renderSortButton("equipmentPlanNumber")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterEquipmentPlanNumber} onChange={setFilterEquipmentPlanNumber} />
                        <ResizeHandle colIndex={8} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[9] ? `${colWidths[9]}px` : "11%"} position="relative">
                        {t('common.field.mmiProductName')}{renderSortButton("mmiProductName")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterMMIProductName} onChange={setFilterMMIProductName} />
                        <ResizeHandle colIndex={9} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[10] ? `${colWidths[10]}px` : "12%"} position="relative">
                        {t('common.field.customerProductName')}{renderSortButton("customerProductName")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterCustomerProductName} onChange={setFilterCustomerProductName} />
                        <ResizeHandle colIndex={10} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[11] ? `${colWidths[11]}px` : "10%"} position="relative">
                        {t('common.field.installationFactory')}{renderSortButton("installationFactory")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterInstallationFactory} onChange={setFilterInstallationFactory} />
                        <ResizeHandle colIndex={11} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[12] ? `${colWidths[12]}px` : "12%"} position="relative">
                        {t('common.field.registrant')}{renderSortButton("registrant")}
                        <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterRegistrant} onChange={setFilterRegistrant} />
                        <ResizeHandle colIndex={12} setColWidths={setColWidths} />
                      </Th>
                      <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" width={colWidths[13] ? `${colWidths[13]}px` : "16%"} position="relative">
                        {t('common.field.approveDate')}{renderSortButton("approveDay")}
                        <Flex mt={1} align="center" gap={1}>
                          <Input type="date" size="xs" bg="white" fontWeight="normal" flex={1} value={formatDateForInput(filterApplicationStartDate)} onChange={(e) => setFilterApplicationStartDate(parseDateFromInput(e.target.value))} />
                          <Text fontSize="xs" fontWeight="normal" color="gray.500">〜</Text>
                          <Input type="date" size="xs" bg="white" fontWeight="normal" flex={1} value={formatDateForInput(filterApplicationEndDate)} onChange={(e) => setFilterApplicationEndDate(parseDateFromInput(e.target.value))} />
                        </Flex>
                        <ResizeHandle colIndex={13} setColWidths={setColWidths} />
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredInvestmentTargets.map(investmentTarget => (
                      <Tr key={investmentTarget.projectId} _hover={{ bg: "gray.50" }}>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.projectName || ""}>
                            <Link
                              as={RouterLink}
                              to={ApplicationPathName.InvestmentScreeningDetail.Url}
                              state={{ projectId: investmentTarget.projectId }}
                              textColor={'blue.500'}
                              textDecoration={'underline'}
                              onClick={(e) => openInvestmentScreeningDetailInNewTab(e, investmentTarget.projectId || "")}
                              onAuxClick={(e) => openInvestmentScreeningDetailInNewTab(e, investmentTarget.projectId || "")}
                            >
                              {investmentTarget.projectName}
                            </Link>
                          </TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.purpose || ""}>{investmentTarget.purpose}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={getEquipmentUsageTypeNameByCode(investmentTarget.equipmentUsageType || "") || ""}>{getEquipmentUsageTypeNameByCode(investmentTarget.equipmentUsageType || "")}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={getHeadquartersNameFromHierarchy(investmentTarget)}>{getHeadquartersNameFromHierarchy(investmentTarget)}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={getDivisionNameFromHierarchy(investmentTarget)}>{getDivisionNameFromHierarchy(investmentTarget)}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.customerName || ""}>{investmentTarget.customerName}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.opportunityName || ""}>{investmentTarget.opportunityName}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.applicationNumber || ""}>{investmentTarget.applicationNumber}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={formatMultiValue(investmentTarget.equipmentPlanNumber)}>{formatMultiValue(investmentTarget.equipmentPlanNumber)}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.mmiProductName || ""}>{investmentTarget.mmiProductName}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.customerProductName || ""}>{investmentTarget.customerProductName}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.installationFactory || ""}>{investmentTarget.installationFactory}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200">
                          <TruncatedCell label={investmentTarget.registrant || ""}>{investmentTarget.registrant}</TruncatedCell>
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="left">
                          <TruncatedCell label={investmentTarget.approveDay || ""}>{investmentTarget.approveDay}</TruncatedCell>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel p={0} pt={4} display="flex" flexDirection="column" flex={1} overflow="auto">
            <ReviewSearchTab
              accountingOrganizations={accountingOrganizations}
              conditionFiscalPeriod={conditionFiscalPeriod}
              onFiscalPeriodChange={handleFiscalPeriodChange}
              onResetFiscalPeriod={handleResetFiscalPeriod}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default InvestmentList;
